import type { Tag } from 'src/types/helios/Tags';
import type { TagMap } from 'src/types/helios/Question';

/** A single selectable leaf option: a display `label` and an encoded `value`
 *  that carries both its tag group and its value. */
export interface TagLeafOption {
  readonly label: string;
  readonly value: string;
}

/** A tag group with its selectable leaf values. Stencil `Select` auto-detects
 *  this shape (an entry with both `groupName` and `values`) and renders a
 *  group header with the values indented underneath — matching the manual
 *  Cloudscape grouped multi-select. */
export interface TagOptionGroup {
  readonly groupName: string;
  readonly values: readonly TagLeafOption[];
}

/** The decoded `(group, value)` pair a leaf option's `value` carries. */
export interface TagPair {
  readonly group: string;
  readonly value: string;
}

/**
 * Encode a `(group, value)` pair into a single option `value` string. JSON is
 * used (not string concatenation) so a group or value containing the other as
 * a substring can't corrupt the round-trip — the flaw in the manual flow's
 * `value.substring(0, len - label.length)` decode.
 */
export function encodeTagPair(group: string, value: string): string {
  return JSON.stringify({ group, value });
}

/**
 * Decode a leaf option `value` back into its `(group, value)` pair. Returns
 * `null` on any malformed payload so a corrupt value can never crash the
 * caller (mirrors the perform-audit filter panel's defensive decode).
 */
export function decodeTagPair(encoded: string): TagPair | null {
  try {
    const parsed: unknown = JSON.parse(encoded);
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'group' in parsed &&
      'value' in parsed &&
      typeof (parsed as { group: unknown }).group === 'string' &&
      typeof (parsed as { value: unknown }).value === 'string'
    ) {
      return {
        group: (parsed as { group: string }).group,
        value: (parsed as { value: string }).value,
      };
    }
    return null;
  } catch {
    return null;
  }
}

const COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base' });

/**
 * Build the grouped option universe from the tag catalog. Only active tags and
 * active values are offered; groups and values are alphabetised for a stable
 * render order. Each leaf's `value` is the encoded `(group, value)` pair.
 */
export function tagsToOptionGroups(tags: readonly Tag[]): TagOptionGroup[] {
  return tags
    .filter((tag) => tag.isActive && tag.tagGroupName)
    .map((tag) => {
      const groupName = tag.tagGroupName ?? '';
      return {
        groupName,
        values: Object.entries(tag.values)
          .filter(([, v]) => v.isActive)
          .map(([key]) => key)
          .sort((a, b) => COLLATOR.compare(a, b))
          .map((valueKey) => ({
            label: valueKey,
            value: encodeTagPair(groupName, valueKey),
          })),
      };
    })
    .filter((group) => group.values.length > 0)
    .sort((a, b) => COLLATOR.compare(a.groupName, b.groupName));
}

/**
 * Project a stored `TagMap` (`{ [group]: values[] }`) onto the flat list of
 * encoded leaf `value`s the grouped select consumes for its `values` prop.
 */
export function tagMapToSelectedValues(tagMap: TagMap): string[] {
  return Object.entries(tagMap).flatMap(([group, values]) =>
    values.map((value) => encodeTagPair(group, value)),
  );
}

/**
 * Rebuild a `TagMap` from the grouped select's flat selected leaf `value`s,
 * regrouping by tag group. Malformed encodings are dropped. De-dupes values
 * within a group so a doubled selection can't produce duplicate entries.
 */
export function selectedValuesToTagMap(encodedValues: readonly string[]): TagMap {
  const byGroup = new Map<string, Set<string>>();
  for (const encoded of encodedValues) {
    const pair = decodeTagPair(encoded);
    if (!pair) continue;
    let bucket = byGroup.get(pair.group);
    if (!bucket) {
      bucket = new Set<string>();
      byGroup.set(pair.group, bucket);
    }
    bucket.add(pair.value);
  }
  return Object.fromEntries(
    Array.from(byGroup, ([group, valueSet]) => [group, Array.from(valueSet)]),
  );
}
